import clsx from 'clsx'
import debounce from 'lodash/debounce'
import React, { useEffect, useRef, useState } from 'react'
import { Control } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'

import s from './styles.module.scss'

import { AdultsPickerModeEnum } from './adults-picker.enum'
import { getAdultsPickerClassNameByEnum } from './adults-picker.utils'
import { AdultsPickerContentModal } from './AdultsPickerContentModal'
import { AdultsPickerInputField } from './AdultsPickerInputField'
import { ModalPicker, ModalPickerRef } from '../modal-picker'
import { NumberInputRangeRef } from '../number-input-range/number-input-range'
import { Button } from '../../atoms'
import { ControlledCommons } from '../../atoms/fields/common.interface'
import { useTravelTypeContext } from '../../contexts'

export interface IControlledAdultsPickerProps extends ControlledCommons {
  control: Control<any, any>
  rules: any
  name: any
  className?: string
  modalClassName?: string
  maxAdults?: number
  isCentered?: boolean
  isMobile?: boolean
  alternativeClassName?: string
  mode?: AdultsPickerModeEnum
  revampPicker?: boolean
  isFullScreen?: boolean
  isOpen?: boolean
  onValid?: () => void
  onClose?: () => void
}

export default function AdultsPicker({
  control,
  rules,
  name,
  className,
  modalClassName,
  maxAdults,
  isCentered = true,
  isMobile = false,
  alternativeClassName = 'default',
  mode = AdultsPickerModeEnum.DEFAULT,
  revampPicker = false,
  isFullScreen,
  isOpen = false,
  onClose,
  onValid,
}: IControlledAdultsPickerProps) {
  const ref = useRef<ModalPickerRef>(null)
  const refNumberInputRange = useRef<NumberInputRangeRef>(null)
  const [isAdultsSelected, setIsAdultsSelected] = useState(false)
  const [isFocused, setIsFocused] = useState<boolean>(false)

  const { travelType } = useTravelTypeContext()

  const debouncedOnChange = useRef(
    debounce((field, value) => {
      field.onChange(value)
      setIsAdultsSelected(value > 0)
    }, 300),
  ).current

  const onFocused = () => {
    setIsFocused(true)
  }
  const onCloseModal = () => {
    onClose?.()
    ref.current?.setShowModal(false)
    setIsFocused(false)
  }
  const onIsAdultSelected = (value: boolean) => {
    setIsAdultsSelected(value)
  }
  const handleClickMobileFooter = () => {
    onValid?.()
    onCloseModal()
  }

  useEffect(() => {
    if (isOpen) {
      ref.current?.setShowModal(true)
    }
  }, [isOpen])

  return (
    <ModalPicker
      revampPicker={revampPicker}
      isFullScreen={isFullScreen}
      dataGtm={
        revampPicker ? 'nombre_voyageurs_new_search' : 'nombre_voyageurs'
      }
      dataMixpanel={{
        name: 'nombre_voyageurs',
        data: { newSearch: revampPicker },
      }}
      control={control}
      rules={rules}
      name={name}
      ref={ref}
      mobileTitle={
        <div>
          {revampPicker ? (
            <FormattedMessage
              id='ui.AdultsPicker.mobileRevampTitle'
              defaultMessage='Nombre de participants'
            />
          ) : (
            <FormattedMessage
              id='ui.AdultsPicker.mobileTitle'
              defaultMessage='Nombre de voyageurs'
            />
          )}
        </div>
      }
      onClickOutside={() => {
        if (
          refNumberInputRange.current &&
          refNumberInputRange.current.onChange
        ) {
          refNumberInputRange.current.onChange()
        }
        setIsFocused(false)
      }}
      mobileFooter={
        <div className={s.mobileFooter}>
          <Button
            onClick={handleClickMobileFooter}
            style={{ padding: '10px 20px' }}
            disabled={!isAdultsSelected && revampPicker}
          >
            <FormattedMessage
              id='ui.AdultsPicker.save'
              defaultMessage='Enregistrer'
            />
          </Button>
        </div>
      }
      className={clsx(className)}
      containerClassName={clsx(
        s[getAdultsPickerClassNameByEnum(mode) + 'Container'],
        {
          [s.focused]: isFocused,
        },
      )}
      modalClassName={clsx(
        modalClassName
          ? modalClassName
          : s[getAdultsPickerClassNameByEnum(mode)],
        s[`${alternativeClassName}_modal`],
      )}
      isCentered={isCentered}
      onClose={onCloseModal}
      renderContentModal={(field) => (
        <AdultsPickerContentModal
          revampPicker={revampPicker}
          field={field}
          onIsAdultSelected={onIsAdultSelected}
          maxAdults={maxAdults}
          isMobile={isMobile}
          onCloseModal={onCloseModal}
          onChange={(value) => {
            debouncedOnChange(field, value)
          }}
          ref={refNumberInputRange}
          onValid={onValid}
        />
      )}
      onOpen={onFocused}
      renderInputField={(field) => (
        <AdultsPickerInputField
          alternativeClassName={alternativeClassName}
          field={field}
          isMobile={isMobile}
          revampPicker={revampPicker}
          mode={mode}
          isFocused={isFocused}
          isAdultsSelected={isAdultsSelected}
          travelType={travelType}
        />
      )}
    />
  )
}
